import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Save, Plus, Trash2, ChevronDown, ChevronUp, Users, Cloud, BookOpen, Bookmark, GripVertical, Flag, AlertTriangle, Eye, EyeOff, Upload, Image as ImageIcon, MapPin } from "lucide-react";
import CallSheetHtmlPreview from "../components/CallSheetHtmlPreview";
import BannerIcon, { BANNER_ICON_OPTIONS } from "../components/BannerIcon";
import LocationAutocomplete from "../components/LocationAutocomplete";
import CastCallTimesSection from "./callsheet-sections/CastCallTimesSection";
import CrewAssignmentsSection from "./callsheet-sections/CrewAssignmentsSection";
import KeyContactsSection from "./callsheet-sections/KeyContactsSection";
import TodaysScheduleSection from "./callsheet-sections/TodaysScheduleSection";
import StandInsBackgroundSection from "./callsheet-sections/StandInsBackgroundSection";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ax = () => ({ withCredentials: true });

const DEFAULT_DEPTS = [
  { department: "Camera", call_time: "", notes: "" },
  { department: "Grip", call_time: "", notes: "" },
  { department: "Electric", call_time: "", notes: "" },
  { department: "Sound", call_time: "", notes: "" },
  { department: "Art Dept", call_time: "", notes: "" },
  { department: "Hair & Makeup", call_time: "", notes: "" },
  { department: "Wardrobe", call_time: "", notes: "" },
  { department: "Catering", call_time: "", notes: "" },
  { department: "Transportation", call_time: "", notes: "" },
  { department: "Production", call_time: "", notes: "" },
];

const CLIENT_TOGGLEABLE = {
  phone: "Phone",
  email: "Email",
  notes: "Notes",
};

const CLIENT_HEADERS = [
  { key: "name", label: "Name" },
  { key: "title", label: "Title" },
  { key: "phone", label: "Phone" },
  { key: "email", label: "Email" },
  { key: "call_time", label: "Call" },
  { key: "notes", label: "Notes" },
  { key: "_del", label: "" },
];

// Time & schedule helpers
function parseDurationMin(s) {
  if (!s) return 0;
  s = String(s).trim().toLowerCase();
  if (!s) return 0;
  if (s.includes(":")) {
    const [h, m] = s.split(":");
    return (parseInt(h, 10) || 0) * 60 + (parseInt(m, 10) || 0);
  }
  let total = 0;
  const hm = s.match(/(\d+)\s*h/); const mm = s.match(/(\d+)\s*m(?!i?d)/);
  if (hm) total += parseInt(hm[1], 10) * 60;
  if (mm) total += parseInt(mm[1], 10);
  if (total) return total;
  if (/^\d+$/.test(s)) return parseInt(s, 10);
  return 0;
}
function fmtMinutes(m) {
  if (m <= 0) return "0m";
  const h = Math.floor(m / 60), r = m % 60;
  if (h && r) return `${h}h ${r}m`;
  return h ? `${h}h` : `${r}m`;
}
function parsePagesEighths(s) {
  if (!s) return 0;
  const str = String(s).trim();
  const mixed = str.match(/^\s*(?:(\d+)\s+)?(\d+)\s*\/\s*(\d+)\s*$/);
  if (mixed) {
    const whole = parseInt(mixed[1] || "0", 10);
    const num = parseInt(mixed[2], 10);
    const den = parseInt(mixed[3] || "1", 10);
    if (den === 0) return whole * 8;
    return whole * 8 + Math.round((num * 8) / den);
  }
  const dec = str.match(/^\s*(\d+)(?:\.(\d+))?\s*$/);
  if (dec) {
    const whole = parseInt(dec[1], 10);
    const frac = dec[2] ? parseFloat("0." + dec[2]) : 0;
    return whole * 8 + Math.round(frac * 8);
  }
  return 0;
}
function fmtPages(eighths) {
  if (eighths <= 0) return "0";
  const whole = Math.floor(eighths / 8), rem = eighths % 8;
  if (rem === 0) return String(whole);
  const gcd = (a, b) => (b === 0 ? a : gcd(b, a % b));
  const g = gcd(rem, 8);
  const n = rem / g, d = 8 / g;
  return whole ? `${whole} ${n}/${d}` : `${n}/${d}`;
}
function addTimeOffset(baseHHMM, offset) {
  if (!baseHHMM || !offset) return "";
  const m = String(baseHHMM).match(/^\s*(\d{1,2}):(\d{2})\s*$/);
  if (!m) return "";
  const baseMin = parseInt(m[1], 10) * 60 + parseInt(m[2], 10);
  let off = offset.trim().toLowerCase();
  const sign = off.startsWith("-") ? -1 : 1;
  off = off.replace(/^[+-]/, "").trim();
  const delta = parseDurationMin(off);
  if (delta === 0 && !off) return "";
  let total = (baseMin + sign * delta) % (24 * 60);
  if (total < 0) total += 24 * 60;
  return `${String(Math.floor(total / 60)).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`;
}

// Meal-penalty check: first meal must be within 6h of general call (always on)
const MEAL_PENALTY_LIMIT_MIN = 6 * 60;
function unionThresholdMin(/* unionType */) {
  return MEAL_PENALTY_LIMIT_MIN;
}
function bannerOffsetMinutes(banner) {
  if (!banner) return null;
  const off = (banner.banner_offset || "").trim();
  if (!off) return null;
  const sign = off.startsWith("-") ? -1 : 1;
  const cleaned = off.replace(/^[+-]/, "").trim();
  const mins = parseDurationMin(cleaned);
  return sign * mins;
}
function isMealBanner(banner) {
  const label = (banner?.banner_label || banner?.description || "").toLowerCase();
  return /\b(lunch|meal|breakfast|dinner|2nd\s*meal|second\s*meal)\b/.test(label);
}
function mealPenaltyMinutes(banner) {
  const limit = MEAL_PENALTY_LIMIT_MIN;
  if (!isMealBanner(banner)) return 0;
  const m = bannerOffsetMinutes(banner);
  if (m === null) return 0;
  return Math.max(0, m - limit);
}

// Stopwatch fmt: seconds → "H:MM:SS" or "M:SS"
function fmtStopwatch(secs) {
  secs = Math.max(0, Math.floor(secs));
  const h = Math.floor(secs / 3600), m = Math.floor((secs % 3600) / 60), s = secs % 60;
  const mm = String(m).padStart(2, "0"), ss = String(s).padStart(2, "0");
  return h > 0 ? `${h}:${mm}:${ss}` : `${m}:${ss}`;
}


const BLANK_CS = {
  production_name: "", shoot_date: "", day_of_days: "", general_call: "",
  director: "", producer: "", first_ad: "", unit_pm: "",
  location_name: "", location_address: "", parking_address: "",
  production_office_address: "",
  nearest_hospital: { name: "", address: "", phone: "", er_notes: "" },
  weather: { conditions: "", sunrise: "", sunset: "", temperature: "", humidity: "",
             temp_high: "", temp_low: "", description: "", icon: "" },
  scenes: [], cast_calls: [], dept_calls: DEFAULT_DEPTS, crew_assignments: [],
  clients: [], stand_ins_background: [], key_contacts: [],
  hidden_client_columns: [],
  general_notes: "", safety_notes: "", covid_protocols: "",
  walkie_channels: "", quote_of_the_day: "",
  footer_left_text: "",
  production_reminders: [],
  hidden_schedule_columns: [],
  hidden_cast_columns: [],
  show_cast_email: false,
  show_cast_phone: false,
  show_crew_email: false,
  show_crew_phone: false,
  department_call_times: {},
  status: "draft",
};

const SCHEDULE_COLS = [
  { key: "time", label: "TIME" },
  { key: "scene_num", label: "SC#" },
  { key: "int_ext", label: "I/E" },
  { key: "day_night", label: "D/N" },
  { key: "set_name", label: "Set" },
  { key: "description", label: "Description" },
  { key: "cast_ids", label: "Cast IDs" },
  { key: "pages", label: "Pages" },
  { key: "location", label: "Location" },
  { key: "duration", label: "Dur" },
];

const DEFAULT_REMINDERS = [
  "No visitors without prior approval of Production",
  "No smoking on set",
  "Wear closed-toed shoes",
  "No photos on set",
  "Put cellphones on silent when on set",
  "No forced calls or OT without prior approval of Production",
  "Report injuries to the Set medic",
  "Remember to stay hydrated",
  "Be quiet when approaching the set",
];

function Section({ title, children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  // Auto-open when data lands (e.g. after the callsheet fetch flips defaultOpen from false → true).
  // Never force-collapse when defaultOpen goes back to false — respect the user's manual state.
  const prevRef = useRef(defaultOpen);
  useEffect(() => {
    if (defaultOpen && !prevRef.current) setOpen(true);
    prevRef.current = defaultOpen;
  }, [defaultOpen]);
  return (
    <div className="bg-[#1A1F29] border border-[#28303F] rounded-xl overflow-hidden">
      <button onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-[#1E2530] transition-colors">
        <h3 className="text-sm font-semibold text-white uppercase tracking-widest font-mono">{title}</h3>
        {open ? <ChevronUp size={16} className="text-gray-500" /> : <ChevronDown size={16} className="text-gray-500" />}
      </button>
      {open && <div className="px-5 pb-5 border-t border-[#28303F]">{children}</div>}
    </div>
  );
}

function F({ label, value, onChange, type = "text", span = 1, placeholder = "" }) {
  return (
    <div className={span > 1 ? `col-span-${span}` : ""}>
      <label className="block text-xs text-gray-500 mb-1.5 uppercase tracking-wider">{label}</label>
      {type === "textarea" ? (
        <textarea value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} rows={3}
          className="w-full bg-[#0D0F12] border border-[#28303F] rounded-lg px-3 py-2.5 text-white placeholder-gray-700 focus:outline-none focus:border-amber-500 text-sm resize-none" />
      ) : (
        <input type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
          className="w-full bg-[#0D0F12] border border-[#28303F] rounded-lg px-3 py-2.5 text-white placeholder-gray-700 focus:outline-none focus:border-amber-500 text-sm" />
      )}
    </div>
  );
}

function Select({ label, value, onChange, options }) {
  return (
    <div>
      <label className="block text-xs text-gray-500 mb-1.5 uppercase tracking-wider">{label}</label>
      <select value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full bg-[#0D0F12] border border-[#28303F] rounded-lg px-3 py-2.5 text-white focus:outline-none focus:border-amber-500 text-sm">
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}

export default function CallSheetBuilder() {
  const { projectId, callsheetId } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState({ ...BLANK_CS, project_id: projectId || "" });
  const [project, setProject] = useState(null);
  const [crew, setCrew] = useState([]);
  const [saving, setSaving] = useState(false);
  const [showCrewPicker, setShowCrewPicker] = useState(false);
  const [crewPickerMode, setCrewPickerMode] = useState("crew"); // "crew" | "key_contacts"
  const [templates, setTemplates] = useState([]);
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);
  const [showSaveTemplate, setShowSaveTemplate] = useState(false);
  const [templateName, setTemplateName] = useState("");
  const [templateDesc, setTemplateDesc] = useState("");
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [fetchingWeather, setFetchingWeather] = useState(false);
  const [weatherError, setWeatherError] = useState("");
  const [findingHospital, setFindingHospital] = useState(false);
  const [parkingSameAsBasecamp, setParkingSameAsBasecamp] = useState(false);
  const [locations, setLocations] = useState([]);
  const [showSaveLocation, setShowSaveLocation] = useState(false);
  const [locationName, setLocationName] = useState("");
  const [savingLocation, setSavingLocation] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const logoInputRef = useRef(null);
  const [showPreview, setShowPreview] = useState(() => {
    try { return localStorage.getItem("cs_builder_preview") !== "0"; } catch { return true; }
  });
  const [previewZoom, setPreviewZoom] = useState(() => {
    try { return parseFloat(localStorage.getItem("cs_builder_preview_zoom") || "0.65"); } catch { return 0.65; }
  });
  useEffect(() => { try { localStorage.setItem("cs_builder_preview", showPreview ? "1" : "0"); } catch {} }, [showPreview]);
  useEffect(() => { try { localStorage.setItem("cs_builder_preview_zoom", String(previewZoom)); } catch {} }, [previewZoom]);

  useEffect(() => {
    const pid = projectId || form.project_id;
    if (pid) axios.get(`${API}/projects/${pid}`, ax()).then(r => {
      setProject(r.data);
      setForm(f => ({
        ...f,
        project_id: pid,
        production_name: f.production_name || r.data.name,
        director: f.director || r.data.director,
        producer: f.producer || r.data.producer,
        first_ad: f.first_ad || r.data.first_ad,
        unit_pm: f.unit_pm || r.data.unit_pm,
      }));
    }).catch(() => {});
    const crewUrl = pid ? `${API}/projects/${pid}/crew` : `${API}/crew`;
    axios.get(crewUrl, ax()).then(r => setCrew(r.data)).catch(() => {});
    axios.get(`${API}/templates`, ax()).then(r => setTemplates(r.data)).catch(() => {});
    const locationsUrl = pid ? `${API}/projects/${pid}/locations` : `${API}/locations`;
    axios.get(locationsUrl, ax()).then(r => setLocations(r.data)).catch(() => {});
    if (callsheetId) {
      axios.get(`${API}/callsheets/${callsheetId}`, ax()).then(r => {
        const cs = r.data;
        if (!cs.dept_calls || cs.dept_calls.length === 0) cs.dept_calls = DEFAULT_DEPTS;
        setForm(cs);
      }).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, callsheetId, form.project_id]);

  const applyLocation = (id) => {
    if (!id) return;
    const l = locations.find(x => x.id === id);
    if (!l) return;
    setForm(f => ({
      ...f,
      location_name: l.location_name || f.location_name,
      location_address: l.location_address || f.location_address,
      parking_address: l.parking_address || f.parking_address,
      nearest_hospital: {
        name: l.hospital_name || f.nearest_hospital?.name || "",
        address: l.hospital_address || f.nearest_hospital?.address || "",
        phone: l.hospital_phone || f.nearest_hospital?.phone || "",
        er_notes: l.hospital_er_notes || f.nearest_hospital?.er_notes || "",
      },
    }));
  };

  const saveAsLocationPreset = async () => {
    if (!locationName.trim()) return alert("Preset name is required");
    setSavingLocation(true);
    try {
      const payload = {
        name: locationName.trim(),
        location_name: form.location_name || "",
        location_address: form.location_address || "",
        parking_address: form.parking_address || "",
        hospital_name: form.nearest_hospital?.name || "",
        hospital_address: form.nearest_hospital?.address || "",
        hospital_phone: form.nearest_hospital?.phone || "",
        hospital_er_notes: form.nearest_hospital?.er_notes || "",
      };
      const endpoint = form.project_id
        ? `${API}/projects/${form.project_id}/locations`
        : `${API}/locations`;
      const { data } = await axios.post(endpoint, payload, ax());
      setLocations(prev => [...prev, data].sort((a, b) => a.name.localeCompare(b.name)));
      setShowSaveLocation(false); setLocationName("");
      alert(`Location preset "${data.name}" saved.`);
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to save preset");
    } finally { setSavingLocation(false); }
  };

  const deleteLocationPreset = async (id) => {
    if (!window.confirm("Delete this location preset?")) return;
    const endpoint = form.project_id
      ? `${API}/projects/${form.project_id}/locations/${id}`
      : `${API}/locations/${id}`;
    await axios.delete(endpoint, ax());
    setLocations(prev => prev.filter(l => l.id !== id));
  };

  const fetchWeather = async () => {
    const loc = (form.location_address || "").trim();
    if (!loc) {
      setWeatherError("Enter a Basecamp address in the Location section first, then click Auto-fill.");
      return;
    }
    setFetchingWeather(true);
    setWeatherError("");
    try {
      const { data } = await axios.get(`${API}/weather`, {
        params: { location: loc, date: form.shoot_date || "" },
        withCredentials: true,
      });
      setForm(f => ({
        ...f,
        weather: {
          conditions: data.conditions || "",
          sunrise: data.sunrise || "",
          sunset: data.sunset || "",
          temperature: data.temperature || "",
          humidity: data.humidity || "",
          temp_high: data.temp_high || "",
          temp_low: data.temp_low || "",
          description: data.description || "",
          icon: data.icon || "",
          fetched_at: new Date().toISOString(),
          fetched_for_date: form.shoot_date || "",
        },
      }));
    } catch (e) {
      const detail = e.response?.data?.detail || e.message || "Failed to fetch weather";
      const isQuota = /limit|quota|rate|exceed|429/i.test(String(detail));
      setWeatherError(
        isQuota
          ? "The free weather service has hit its daily request limit. You can type the values in manually below — the fields are editable."
          : `${detail} You can enter the values manually below.`
      );
    } finally { setFetchingWeather(false); }
  };

  const [hospitalAlternates, setHospitalAlternates] = useState([]);
  const applyHospital = (h) => {
    setForm(f => ({
      ...f,
      nearest_hospital: {
        name: h.name || "",
        address: h.address || "",
        phone: h.phone || "",
        er_notes: "",
      },
    }));
  };

  const findNearestHospital = async () => {
    const loc = (form.location_address || "").trim();
    if (!loc) return alert("Enter a Basecamp address above first, then click Find Nearest.");
    setFindingHospital(true);
    try {
      const { data } = await axios.get(`${API}/hospital/nearest`, {
        params: { location: loc },
        withCredentials: true,
      });
      applyHospital(data);
      setHospitalAlternates(data.alternates || []);
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to find nearest hospital");
    } finally { setFindingHospital(false); }
  };


  useEffect(() => {
    if (parkingSameAsBasecamp && form.location_address !== form.parking_address) {
      setForm(f => ({ ...f, parking_address: f.location_address }));
    }
  }, [parkingSameAsBasecamp, form.location_address]);

  const uploadCallsheetLogo = async (file) => {
    if (!file) return;
    if (!callsheetId) {
      // Read as data URI locally; will be saved on next Save
      const reader = new FileReader();
      reader.onload = () => setForm(f => ({ ...f, logo_data_uri: String(reader.result || "") }));
      reader.readAsDataURL(file);
      return;
    }
    setUploadingLogo(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await axios.post(`${API}/callsheets/${callsheetId}/logo`, fd, {
        withCredentials: true, headers: { "Content-Type": "multipart/form-data" },
      });
      setForm(f => ({ ...f, logo_data_uri: data.logo_data_uri }));
    } catch (e) {
      alert(e.response?.data?.detail || "Logo upload failed");
    } finally { setUploadingLogo(false); }
  };

  const clearCallsheetLogo = async () => {
    if (!window.confirm("Remove this call sheet's custom logo? It'll fall back to the project logo.")) return;
    if (callsheetId) {
      try { await axios.delete(`${API}/callsheets/${callsheetId}/logo`, ax()); } catch {}
    }
    setForm(f => ({ ...f, logo_data_uri: "" }));
  };

  const effectiveLogo = form.logo_data_uri || project?.logo_data_uri || "";
  const logoSource = form.logo_data_uri ? "custom" : (project?.logo_data_uri ? "project" : "none");

  const daysUntilShoot = () => {
    if (!form.shoot_date) return null;
    const d = new Date(form.shoot_date + "T12:00:00");
    if (isNaN(d)) return null;
    const today = new Date(); today.setHours(0,0,0,0);
    return Math.round((d - today) / 86400000);
  };
  const showWeatherStaleBadge = () => {
    const days = daysUntilShoot();
    const hasWeather = !!(form.weather?.conditions || form.weather?.sunrise);
    return days !== null && days > 7 && hasWeather;
  };

  const applyTemplate = (t) => {
    setForm(f => ({
      ...f,
      general_call: f.general_call || t.general_call || "",
      nearest_hospital: (f.nearest_hospital?.name || f.nearest_hospital?.address)
        ? f.nearest_hospital
        : (t.nearest_hospital || f.nearest_hospital),
      dept_calls: (t.dept_calls && t.dept_calls.length > 0) ? t.dept_calls : f.dept_calls,
      general_notes: f.general_notes || t.general_notes || "",
      safety_notes: f.safety_notes || t.safety_notes || "",
      covid_protocols: f.covid_protocols || t.covid_protocols || "",
    }));
    setShowTemplatePicker(false);
  };

  const saveAsTemplate = async () => {
    if (!templateName.trim()) return alert("Template name is required");
    setSavingTemplate(true);
    try {
      const payload = {
        name: templateName.trim(),
        description: templateDesc.trim(),
        general_call: form.general_call || "",
        nearest_hospital: form.nearest_hospital || {},
        dept_calls: form.dept_calls || [],
        general_notes: form.general_notes || "",
        safety_notes: form.safety_notes || "",
        covid_protocols: form.covid_protocols || "",
      };
      const { data } = await axios.post(`${API}/templates`, payload, ax());
      setTemplates(prev => [data, ...prev]);
      setShowSaveTemplate(false);
      setTemplateName(""); setTemplateDesc("");
      alert(`Template "${data.name}" saved.`);
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to save template");
    } finally { setSavingTemplate(false); }
  };

  const deleteTemplate = async (id) => {
    if (!window.confirm("Delete this template?")) return;
    await axios.delete(`${API}/templates/${id}`, ax());
    setTemplates(prev => prev.filter(t => t.id !== id));
  };

  const set = (k) => (v) => setForm(f => ({ ...f, [k]: v }));
  const setNested = (k, sub) => (v) => setForm(f => ({ ...f, [k]: { ...f[k], [sub]: v } }));

  // Dynamic array helpers


  const addClient = () => setForm((current) => ({
    ...current,
    clients: [
      ...(current.clients || []),
      { name: "", title: "", call_time: "", email: "", phone: "", notes: "" },
    ],
  }));
  const updClient = (i, k, v) => setForm(f => ({ ...f, clients: (f.clients || []).map((c, idx) => idx === i ? { ...c, [k]: v } : c) }));
  const delClient = (i) => setForm(f => ({
    ...f,
    clients: (f.clients || []).filter((_, idx) => idx !== i),
  }));
  const hiddenClientColumns = form.hidden_client_columns || [];
  const clientColumnClass = (key) => (
    hiddenClientColumns.includes(key) ? "opacity-40 pointer-events-none" : ""
  );
  const toggleClientColumn = (key) => setForm((current) => {
    const hidden = current.hidden_client_columns || [];
    return {
      ...current,
      hidden_client_columns: hidden.includes(key)
        ? hidden.filter((column) => column !== key)
        : [...hidden, key],
    };
  });
  const addStandInBackground = (type) => setForm((current) => ({
    ...current,
    stand_ins_background: [
      ...(current.stand_ins_background || []),
      { type, qty: "", description: "", arrive: "", rdy: "" },
    ],
  }));
  const updateStandInBackground = (index, field, value) => setForm((current) => ({
    ...current,
    stand_ins_background: (current.stand_ins_background || []).map((row, rowIndex) => (
      rowIndex === index ? { ...row, [field]: value } : row
    )),
  }));
  const deleteStandInBackground = (index) => setForm((current) => ({
    ...current,
    stand_ins_background: (current.stand_ins_background || []).filter((_, rowIndex) => (
      rowIndex !== index
    )),
  }));


  const addCrewFromDB = (member) => {
    if (crewPickerMode === "key_contacts") {
      if ((form.key_contacts || []).some(c => c.crew_id === member.id)) return;
      setForm(f => ({
        ...f,
        key_contacts: [...(f.key_contacts || []), {
          crew_id: member.id,
          name: member.name || "",
          role: member.role || member.position || "",
          department: member.department || "",
          phone: member.phone || "",
          email: member.email || "",
          sort_order: (f.key_contacts || []).length,
        }],
      }));
      return;
    }
    const already = form.crew_assignments.find(c => c.crew_id === member.id);
    if (already) return;
    const dept = member.department || "";
    const deptDefault = (form.department_call_times || {})[dept] || "";
    setForm(f => ({ ...f, crew_assignments: [...f.crew_assignments, { crew_id: member.id, name: member.name, role: member.role, department: dept, sort_order: f.crew_assignments.length, call_time: deptDefault || form.general_call || "", notes: "", phone: member.phone || "", email: member.email || "" }] }));
  };

  const save = async (status = form.status, { navigateAfter = false } = {}) => {
    setSaving(true);
    try {
      // ── Auto-refresh weather when shoot date is within 48 hours ──
      // Silently re-fetches from NWS so on-set numbers stay fresh. Failures never block save.
      let workingForm = form;
      const hoursOut = (() => {
        if (!form.shoot_date) return null;
        const d = new Date(form.shoot_date + "T12:00:00");
        if (isNaN(d)) return null;
        return (d - new Date()) / 3600000;
      })();
      const basecamp = (form.location_address || "").trim();
      if (basecamp && hoursOut !== null && hoursOut <= 48 && hoursOut >= -6) {
        try {
          const { data } = await axios.get(`${API}/weather`, {
            params: { location: basecamp, date: form.shoot_date || "" },
            withCredentials: true,
          });
          const freshWeather = {
            conditions: data.conditions || "",
            sunrise: data.sunrise || "",
            sunset: data.sunset || "",
            temperature: data.temperature || "",
            humidity: data.humidity || "",
            temp_high: data.temp_high || "",
            temp_low: data.temp_low || "",
            description: data.description || "",
            icon: data.icon || "",
            fetched_at: new Date().toISOString(),
            fetched_for_date: form.shoot_date || "",
          };
          workingForm = { ...form, weather: freshWeather };
          setForm(workingForm);
        } catch {
          // Silent — save proceeds with the weather values already on the form.
        }
      }

      const { __customReminderInput, ...clean } = workingForm;
      const payload = { ...clean, status };
      // Preview navigation happens regardless of status (draft or published).
      // "Save Draft" uses the in-place toast; "Preview" and "Publish" navigate.
      const goToPreview = navigateAfter || status !== "draft";
      if (callsheetId) {
        const { data } = await axios.put(`${API}/callsheets/${callsheetId}`, payload, ax());
        if (goToPreview) {
          navigate(`/callsheets/${data.id}/preview`);
        } else {
          setForm(data);
          toast.success("Draft saved");
        }
      } else {
        const { data } = await axios.post(`${API}/callsheets`, payload, ax());
        if (goToPreview) {
          navigate(`/callsheets/${data.id}/preview`);
        } else {
          toast.success("Draft saved");
          navigate(`/callsheets/${data.id}/edit`, { replace: true });
        }
      }
    } catch (e) {
      alert(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const inputCls = "w-full bg-[#0D0F12] border border-[#28303F] rounded px-2.5 py-1.5 text-white text-xs focus:outline-none focus:border-amber-500";
  const selectCls = "bg-[#0D0F12] border border-[#28303F] rounded px-2 py-1.5 text-white text-xs focus:outline-none focus:border-amber-500";

  return (
    <div
      className={`mx-auto flex gap-6 pb-20 p-6 md:p-8 ${
        showPreview ? "max-w-[1700px]" : "max-w-none"
      }`}
    >
      <div
        data-testid="builder-editor-pane"
        className={`min-w-0 flex-1 space-y-5 ${
          showPreview ? "xl:max-w-5xl" : "w-full max-w-none"
        }`}
      >
      {/* Header */}
      <div className="relative z-20 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <button onClick={() => project ? navigate(`/projects/${project.id}`) : navigate("/dashboard")}
            className="flex items-center gap-2 text-gray-500 hover:text-amber-400 text-sm mb-3 transition-colors">
            <ArrowLeft size={15} /> {project?.name || "Back"}
          </button>
          <h1 className="text-3xl font-extrabold text-white font-[Outfit]">
            {callsheetId ? "Edit Call Sheet" : "New Call Sheet"}
          </h1>
        </div>
        <div className="flex flex-wrap gap-2 sm:justify-end">
          <button onClick={() => setShowTemplatePicker(true)}
            data-testid="builder-apply-template-btn"
            className="flex items-center gap-2 border border-[#28303F] text-gray-300 hover:text-amber-400 hover:border-amber-500/30 px-3 py-2 rounded-lg text-sm transition-all">
            <BookOpen size={14}/> Apply Template
          </button>
          <button onClick={() => setShowSaveTemplate(true)}
            data-testid="builder-save-template-btn"
            className="flex items-center gap-2 border border-[#28303F] text-gray-300 hover:text-amber-400 hover:border-amber-500/30 px-3 py-2 rounded-lg text-sm transition-all">
            <Bookmark size={14}/> Save as Template
          </button>
          <button onClick={() => save("draft")} disabled={saving}
            className="border border-[#28303F] text-gray-400 hover:text-white hover:border-gray-500 px-4 py-2 rounded-lg text-sm transition-all disabled:opacity-50">
            Save Draft
          </button>
          <button data-testid="builder-publish-btn" onClick={() => save("draft", { navigateAfter: true })} disabled={saving}
            className="flex items-center gap-2 bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-black font-bold px-5 py-2 rounded-lg text-sm transition-all">
            <Save size={15} /> {saving ? "Saving..." : "Preview"}
          </button>
        </div>
      </div>

      {/* ── PRODUCTION INFO ── */}
      <Section title="Production Info">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 pt-4">
          <div className="col-span-2 md:col-span-3"><F label="Production Name" value={form.production_name} onChange={set("production_name")} /></div>
          <F label="Shoot Date" value={form.shoot_date} onChange={set("shoot_date")} type="date" data-testid="builder-shoot-date-input" />
          <F label="General Call Time" value={form.general_call} onChange={set("general_call")} placeholder="07:00" />
          <F label="Day of Days (e.g. 1 of 30)" value={form.day_of_days} onChange={set("day_of_days")} placeholder="1 of 30" />
        </div>

        {/* Header Logo */}
        <div className="mt-5 flex flex-col items-start gap-4 border-t border-[#28303F] pt-5 sm:flex-row sm:items-center" data-testid="builder-logo-row">
          <div className="text-xs uppercase tracking-wider text-gray-500 sm:min-w-32">Header Logo</div>
          {effectiveLogo ? (
            <div className="flex w-full flex-wrap items-center gap-3 sm:flex-1 sm:flex-nowrap sm:min-w-0">
              <div className="bg-white rounded p-1.5 flex items-center justify-center shrink-0" style={{ minWidth: 90, height: 48 }}>
                <img data-testid="builder-logo-preview" src={effectiveLogo} alt="Header logo"
                     style={{ maxHeight: 40, maxWidth: 130, objectFit: "contain" }} />
              </div>
              <div className="w-full sm:min-w-0 sm:flex-1">
                <p className="text-xs text-gray-400">
                  {logoSource === "custom"
                    ? "Custom logo for this call sheet."
                    : "Using the project logo. Upload a custom one to override for this call sheet only."}
                </p>
              </div>
              <button type="button" onClick={() => logoInputRef.current?.click()} disabled={uploadingLogo}
                data-testid="builder-logo-replace-btn"
                className="text-xs text-amber-400 hover:text-amber-300 border border-amber-500/40 px-3 py-1.5 rounded-lg">
                {uploadingLogo ? "..." : (logoSource === "custom" ? "Replace" : "Upload Custom")}
              </button>
              {logoSource === "custom" && (
                <button type="button" onClick={clearCallsheetLogo}
                  data-testid="builder-logo-clear-btn"
                  className="text-gray-500 hover:text-red-400 p-1.5" title="Remove custom logo (fall back to project)">
                  <Trash2 size={15} />
                </button>
              )}
            </div>
          ) : (
            <button type="button" onClick={() => logoInputRef.current?.click()} disabled={uploadingLogo}
              data-testid="builder-logo-upload-btn"
              className="flex-1 flex items-center justify-center gap-2 border border-dashed border-[#28303F] hover:border-amber-500/50 rounded-lg py-3 text-gray-500 hover:text-amber-400 text-sm transition-all">
              <Upload size={14}/> {uploadingLogo ? "Uploading..." : (project?.logo_data_uri ? "Override project logo for this call sheet only" : "Upload a header logo for this call sheet")}
            </button>
          )}
          <input ref={logoInputRef} type="file" accept="image/png,image/jpeg,image/jpg,image/webp,image/gif"
            data-testid="builder-logo-file-input"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadCallsheetLogo(f); e.target.value = ""; }}
            className="hidden" />
        </div>

        {/* Logo Layout Overrides */}
        {effectiveLogo && (
          <div className="mt-3 flex flex-col flex-wrap gap-3 border-t border-[#28303F] pt-4 sm:flex-row sm:items-center sm:gap-5" data-testid="builder-logo-layout-controls">
            <div className="text-xs uppercase tracking-wider text-gray-500 sm:min-w-32">Logo Layout</div>

            <div className="flex items-center gap-2">
              <span className="text-[11px] text-gray-500 uppercase tracking-wider">Align</span>
              <div className="flex gap-0.5 rounded border border-[#28303F] bg-[#0D0F12] p-0.5" data-testid="builder-logo-align-group">
                {[
                  { v: "", label: "Project" },
                  { v: "left", label: "Left" },
                  { v: "center", label: "Center" },
                  { v: "right", label: "Right" },
                ].map(({ v, label }) => {
                  const active = (form.logo_align || "") === v;
                  const inheritHint = v === "" && project?.logo_align ? ` (${project.logo_align})` : v === "" ? " (center)" : "";
                  return (
                    <button
                      key={v || "inherit"}
                      type="button"
                      data-testid={`builder-logo-align-${v || "inherit"}`}
                      onClick={() => setForm((f) => ({ ...f, logo_align: v }))}
                      className={`px-2.5 py-1 text-[10px] uppercase tracking-wider rounded ${
                        active ? "bg-amber-500 text-black font-bold" : "text-gray-400 hover:text-white"
                      }`}
                    >
                      {label}{v === "" ? inheritHint : ""}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="flex flex-1 items-center gap-3 sm:min-w-[260px]">
              <span className="text-[11px] text-gray-500 uppercase tracking-wider shrink-0">Max Height</span>
              {!form.logo_max_height_pt ? (
                <>
                  <span className="flex-1 font-mono text-xs text-gray-500" data-testid="builder-logo-max-height-inherit-label">
                    Inherit · {project?.logo_max_height_pt || 36}pt
                  </span>
                  <button
                    type="button"
                    data-testid="builder-logo-max-height-override-btn"
                    onClick={() => setForm((f) => ({ ...f, logo_max_height_pt: project?.logo_max_height_pt || 36 }))}
                    className="rounded border border-amber-500/40 px-2.5 py-1 text-[10px] uppercase tracking-wider text-amber-400 hover:bg-amber-500/10"
                  >
                    Override
                  </button>
                </>
              ) : (
                <>
                  <input
                    type="range"
                    min="20"
                    max="60"
                    step="2"
                    value={form.logo_max_height_pt}
                    data-testid="builder-logo-max-height-slider"
                    onChange={(e) => setForm((f) => ({ ...f, logo_max_height_pt: Number(e.target.value) }))}
                    className="flex-1 accent-amber-500"
                  />
                  <span className="font-mono text-xs text-amber-400 w-10 text-right" data-testid="builder-logo-max-height-value">
                    {form.logo_max_height_pt}pt
                  </span>
                  <button
                    type="button"
                    data-testid="builder-logo-max-height-reset-btn"
                    onClick={() => setForm((f) => ({ ...f, logo_max_height_pt: 0 }))}
                    className="text-[10px] uppercase tracking-wider text-gray-500 hover:text-amber-400"
                    title="Reset to project default"
                  >
                    Reset
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </Section>

      {/* ── KEY CONTACTS ── */}
      <KeyContactsSection form={form} setForm={setForm} crew={crew} Section={Section} />

      {/* ── LOCATION & HOSPITAL ── */}
      <Section title="Location & Hospital">
        <div className="pt-4 space-y-3">
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-48">
              <label className="block text-xs text-gray-500 mb-1.5 uppercase tracking-wider">Load Preset</label>
              <select
                data-testid="location-preset-select"
                onChange={(e) => { applyLocation(e.target.value); e.target.value = ""; }}
                defaultValue=""
                className="w-full bg-[#0D0F12] border border-[#28303F] rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:border-amber-500">
                <option value="">{locations.length === 0 ? "No presets yet" : "Choose a saved location…"}</option>
                {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            </div>
            <button type="button"
              data-testid="location-save-preset-btn"
              onClick={() => setShowSaveLocation(true)}
              className="flex items-center gap-2 border border-amber-500/40 text-amber-400 hover:bg-amber-500/10 px-3 py-2.5 rounded-lg text-xs transition-all">
              <Bookmark size={13}/> Save Current as Preset
            </button>
            {locations.length > 0 && (
              <div className="w-full flex flex-wrap gap-1.5">
                {locations.map(l => (
                  <span key={l.id} className="flex items-center gap-1 text-xs bg-[#0D0F12] border border-[#28303F] rounded-full px-2.5 py-1 text-gray-400">
                    {l.name}
                    <button onClick={() => deleteLocationPreset(l.id)} className="text-gray-600 hover:text-red-400 text-xs">×</button>
                  </span>
                ))}
              </div>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-t border-[#28303F] pt-4">
            <div className="col-span-1 md:col-span-2">
              <label className="block text-xs text-gray-500 mb-1.5 uppercase tracking-wider">Basecamp</label>
              <LocationAutocomplete
                value={form.location_address}
                onChange={(v) => set("location_address")(v)}
                locations={locations}
                strict
                placeholder="Type to search saved locations…"
                dataTestid="builder-basecamp-input"
                className="w-full min-h-[42px] bg-[#0D0F12] border border-[#28303F] focus:border-amber-500 outline-none rounded-lg px-3 py-2 text-sm text-white transition-colors"
              />
            </div>
            <div className="col-span-1 md:col-span-2">
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-xs text-gray-500 uppercase tracking-wider">Parking Address</label>
                <label className="flex items-center gap-2 text-xs text-gray-400 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    data-testid="parking-same-as-basecamp-checkbox"
                    checked={parkingSameAsBasecamp}
                    onChange={(e) => {
                      const v = e.target.checked;
                      setParkingSameAsBasecamp(v);
                      if (v) setForm(f => ({ ...f, parking_address: f.location_address || "" }));
                    }}
                    className="w-3.5 h-3.5 accent-amber-500"
                  />
                  <span>Same as basecamp</span>
                </label>
              </div>
              <LocationAutocomplete
                value={form.parking_address}
                onChange={(v) => set("parking_address")(v)}
                locations={locations}
                strict
                disabled={parkingSameAsBasecamp}
                dataTestid="builder-parking-input"
                placeholder={parkingSameAsBasecamp ? "Synced with basecamp above" : "Type to search saved locations…"}
                className="w-full min-h-[42px] bg-[#0D0F12] border border-[#28303F] focus:border-amber-500 outline-none rounded-lg px-3 py-2 text-sm text-white transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              />
            </div>
            <div className="col-span-1 md:col-span-2">
              <label className="block text-xs text-gray-500 mb-1.5 uppercase tracking-wider">Production Office Address</label>
              <LocationAutocomplete
                value={form.production_office_address}
                onChange={(v) => set("production_office_address")(v)}
                locations={locations}
                strict
                placeholder="Type to search saved locations…"
                className="w-full min-h-[42px] bg-[#0D0F12] border border-[#28303F] focus:border-amber-500 outline-none rounded-lg px-3 py-2 text-sm text-white transition-colors"
              />
            </div>
            <div className="col-span-1 md:col-span-2 border-t border-[#28303F] pt-4">
              <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                <p className="text-xs text-amber-400/80 uppercase tracking-widest font-mono">Nearest Hospital</p>
                <button type="button"
                  data-testid="builder-hospital-autofind-btn"
                  onClick={findNearestHospital}
                  disabled={findingHospital || !form.location_address?.trim()}
                  title={form.location_address?.trim() ? "Find the nearest hospital using this call sheet's basecamp" : "Enter a basecamp address above first"}
                  className="flex items-center gap-2 bg-amber-500/10 hover:bg-amber-500/20 text-amber-400 border border-amber-500/30 px-3 py-1.5 rounded-lg text-xs transition-all disabled:opacity-40 disabled:cursor-not-allowed">
                  <MapPin size={13}/> {findingHospital ? "Searching…" : "Find Nearest"}
                </button>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <F label="Hospital Name" value={form.nearest_hospital.name} onChange={setNested("nearest_hospital","name")} />
                <F label="Phone" value={form.nearest_hospital.phone} onChange={setNested("nearest_hospital","phone")} />
                <div className="col-span-2">
                  <label className="block text-xs uppercase tracking-wider text-gray-500 mb-1">Address</label>
                  <textarea
                    data-testid="builder-hospital-address"
                    value={form.nearest_hospital.address}
                    onChange={e => setNested("nearest_hospital","address")(e.target.value)}
                    rows={3}
                    placeholder="Line breaks (Enter) are preserved on the call sheet."
                    className="w-full bg-neutral-900 border border-neutral-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-amber-500 whitespace-pre-wrap"
                  />
                </div>
              </div>
              {hospitalAlternates.length > 0 && (
                <div className="mt-3 border-t border-[#28303F] pt-3" data-testid="hospital-alternates">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-[10px] text-gray-500 uppercase tracking-widest font-mono">Other nearby options</p>
                    <button type="button" onClick={() => setHospitalAlternates([])}
                      className="text-[10px] text-gray-600 hover:text-red-400" title="Dismiss list">
                      Dismiss
                    </button>
                  </div>
                  <div className="space-y-1.5">
                    {hospitalAlternates.map((h, i) => (
                      <button
                        key={i}
                        type="button"
                        data-testid="hospital-alternate-item"
                        onClick={() => { applyHospital(h); setHospitalAlternates(prev => prev.filter((_, idx) => idx !== i)); }}
                        className="w-full flex items-center justify-between bg-[#0D0F12] border border-[#28303F] hover:border-amber-500/40 rounded-lg px-3 py-2 text-left transition-all"
                      >
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-white font-medium truncate">{h.name}</p>
                          <p className="text-[10px] text-gray-500 truncate">
                            {h.address || "(no address on file)"}
                            {h.phone && <span className="text-gray-600"> · {h.phone}</span>}
                          </p>
                        </div>
                        <div className="text-right ml-3 shrink-0">
                          <p className="text-[10px] text-amber-400 font-mono">{h.distance_mi} mi</p>
                          <p className="text-[10px] text-gray-600">Use →</p>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </Section>

      {/* ── WEATHER ── */}
      <Section title="Weather & Sun Times" defaultOpen={false}>
        <div className="pt-4 space-y-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-3 flex-wrap">
              <button type="button" data-testid="builder-weather-autofill-btn"
                onClick={fetchWeather} disabled={fetchingWeather || !form.location_address?.trim()}
                title={form.location_address?.trim() ? "Auto-fill weather using this call sheet's Basecamp" : "Enter a Basecamp address above first"}
                className="flex items-center gap-2 bg-amber-500/10 hover:bg-amber-500/20 text-amber-400 border border-amber-500/30 px-3 py-1.5 rounded-lg text-xs transition-all disabled:opacity-40 disabled:cursor-not-allowed">
                <Cloud size={14}/> {fetchingWeather ? "Fetching..." : "Auto-fill from Basecamp"}
              </button>
              <p className="text-xs text-gray-600">Uses shoot date &amp; Basecamp address.</p>
            </div>
            {showWeatherStaleBadge() && (
              <div data-testid="weather-stale-badge"
                className="flex items-center gap-1.5 bg-amber-500/10 border border-amber-500/40 text-amber-300 text-xs px-3 py-1.5 rounded-full">
                <Cloud size={12}/>
                Shoot is {daysUntilShoot()} days out — re-run weather closer to the date for accuracy
              </div>
            )}
          </div>
          {weatherError && (
            <div data-testid="weather-error-banner"
              className="flex items-start gap-2 bg-amber-500/10 border border-amber-500/40 text-amber-200 text-xs px-3 py-2 rounded-lg">
              <AlertTriangle size={14} className="mt-0.5 shrink-0"/>
              <div className="flex-1 leading-relaxed">{weatherError}</div>
              <button type="button" onClick={() => setWeatherError("")}
                data-testid="weather-error-dismiss"
                className="text-amber-300/70 hover:text-amber-200 text-xs">✕</button>
            </div>
          )}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <F label="Low" value={form.weather.temp_low} onChange={setNested("weather","temp_low")} placeholder="60°F" />
            <F label="High" value={form.weather.temp_high} onChange={setNested("weather","temp_high")} placeholder="71°F" />
            <F label="Sunrise" value={form.weather.sunrise} onChange={setNested("weather","sunrise")} placeholder="5:42 AM" />
            <F label="Sunset" value={form.weather.sunset} onChange={setNested("weather","sunset")} placeholder="8:07 PM" />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-gray-500 mb-1">Description</label>
            <textarea
              data-testid="builder-weather-description"
              value={form.weather.description || ""}
              onChange={e => setNested("weather","description")(e.target.value)}
              rows={2}
              placeholder="Partly cloudy throughout the day. Feels like 65°F. 3% chance of precip. Wind 13mph from SW."
              className="w-full bg-neutral-900 border border-neutral-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-amber-500 whitespace-pre-wrap"
            />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs uppercase tracking-wider text-gray-500 mb-1">Icon</label>
              <select
                data-testid="builder-weather-icon"
                value={form.weather.icon || ""}
                onChange={e => setNested("weather","icon")(e.target.value)}
                className="w-full bg-neutral-900 border border-neutral-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-amber-500">
                <option value="">— None —</option>
                <option value="clear">Clear (Sun)</option>
                <option value="partly_cloudy">Partly Cloudy</option>
                <option value="cloudy">Cloudy</option>
                <option value="fog">Fog</option>
                <option value="rain">Rain</option>
                <option value="heavy_rain">Heavy Rain</option>
                <option value="snow">Snow</option>
                <option value="thunderstorm">Thunderstorm</option>
              </select>
            </div>
            <F label="Conditions (short)" value={form.weather.conditions} onChange={setNested("weather","conditions")} placeholder="Partly cloudy" />
          </div>
        </div>
      </Section>

      {/* ── PRODUCTION REMINDERS ── */}
      <Section title="Production Reminders & Notes" defaultOpen={false}>
        <div className="pt-4 space-y-3">
          <F label="General Notes" value={form.general_notes} onChange={set("general_notes")} type="textarea" />
          <p className="text-xs text-gray-500 font-mono">
            Selected reminders appear in ALL CAPS separated by " | " on the call sheet.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {(() => {
              const selected = form.production_reminders || [];
              const customReminders = selected.filter(r => !DEFAULT_REMINDERS.includes(r));
              const toggle = (text) => setForm(f => {
                const cur = f.production_reminders || [];
                return { ...f, production_reminders: cur.includes(text) ? cur.filter(x => x !== text) : [...cur, text] };
              });
              return (
                <>
                  {DEFAULT_REMINDERS.map((r, i) => {
                    const on = selected.includes(r);
                    return (
                      <label key={i} data-testid={`reminder-toggle-${i}`}
                        className={`flex items-start gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-colors ${on ? "bg-amber-500/10 border-amber-500/40" : "bg-[#141922] border-[#28303F] hover:border-amber-500/30"}`}>
                        <input type="checkbox" checked={on} onChange={() => toggle(r)}
                          className="mt-0.5 accent-amber-500" />
                        <span className="text-xs text-gray-200 leading-snug">{r}</span>
                      </label>
                    );
                  })}
                  {customReminders.map((r, i) => (
                    <label key={`c-${i}`} data-testid={`reminder-custom-${i}`}
                      className="flex items-start gap-2 px-3 py-2 rounded-lg border bg-amber-500/10 border-amber-500/40 cursor-pointer">
                      <input type="checkbox" checked readOnly
                        onClick={() => toggle(r)}
                        className="mt-0.5 accent-amber-500" />
                      <span className="text-xs text-gray-200 leading-snug flex-1">{r}</span>
                      <button type="button" onClick={() => toggle(r)}
                        className="text-red-400 hover:text-red-300 text-xs font-mono">✕</button>
                    </label>
                  ))}
                </>
              );
            })()}
          </div>
          <div className="flex gap-2 pt-2">
            <input data-testid="reminder-custom-input"
              value={form.__customReminderInput || ""}
              onChange={(e) => setForm(f => ({ ...f, __customReminderInput: e.target.value }))}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  const t = (form.__customReminderInput || "").trim();
                  if (t && !(form.production_reminders || []).includes(t)) {
                    setForm(f => ({ ...f, production_reminders: [...(f.production_reminders || []), t], __customReminderInput: "" }));
                  }
                }
              }}
              placeholder="Add custom reminder…"
              className="flex-1 bg-[#141922] border border-[#28303F] rounded-lg px-3 py-2 text-xs text-white placeholder-gray-600 focus:outline-none focus:border-amber-500/40" />
            <button type="button" data-testid="reminder-add-custom"
              onClick={() => {
                const t = (form.__customReminderInput || "").trim();
                if (t && !(form.production_reminders || []).includes(t)) {
                  setForm(f => ({ ...f, production_reminders: [...(f.production_reminders || []), t], __customReminderInput: "" }));
                }
              }}
              className="px-4 py-2 bg-amber-500/20 border border-amber-500/40 text-amber-300 text-xs font-mono rounded-lg hover:bg-amber-500/30">
              ADD
            </button>
          </div>
        </div>
      </Section>

      {/* ── SCENES / TODAY'S SCHEDULE ── */}
      <TodaysScheduleSection form={form} setForm={setForm} project={project} locations={locations}
        inputCls={inputCls} selectCls={selectCls} Section={Section}
        helpers={{ parseDurationMin, fmtMinutes, parsePagesEighths, fmtPages, unionThresholdMin, mealPenaltyMinutes, bannerOffsetMinutes }} />

      {/* ── CAST CALLS ── */}
      <CastCallTimesSection form={form} setForm={setForm} crew={crew}
        inputCls={inputCls} selectCls={selectCls} Section={Section} />

      <StandInsBackgroundSection
        form={form}
        onAdd={addStandInBackground}
        onUpdate={updateStandInBackground}
        onDelete={deleteStandInBackground}
        inputCls={inputCls}
        Section={Section}
      />

      {/* ── CLIENTS ── */}
      <Section title="Clients" defaultOpen={false}>
        <div className="pt-4 space-y-2">
          {(form.clients || []).length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[860px] text-xs">
                <thead>
                  <tr className="border-b border-[#28303F]">
                    {CLIENT_HEADERS.map(({ key, label }) => {
                      const toggle = CLIENT_TOGGLEABLE[key];
                      const hidden = hiddenClientColumns.includes(key);
                      if (!toggle) {
                        return <th key={key} className="break-words pb-2 pr-2 text-left font-medium text-gray-500">{label}</th>;
                      }
                      return (
                        <th key={key} className={`break-words pb-2 pr-2 text-left font-medium text-gray-500 ${hidden ? "opacity-40" : ""}`}>
                          <span className="inline-flex items-center gap-1">
                            {label}
                            <button
                              type="button"
                              data-testid={`client-col-toggle-${key}`}
                              onClick={() => toggleClientColumn(key)}
                              title={hidden ? "Show column on call sheet" : "Hide column from call sheet"}
                              className={`transition-opacity ${hidden ? "text-gray-500 hover:text-amber-400" : "opacity-40 hover:opacity-100 hover:text-amber-400"}`}
                            >
                              {hidden ? <EyeOff size={10} /> : <Eye size={10} />}
                            </button>
                          </span>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {(form.clients || []).map((c, i) => (
                    <tr key={i} data-testid="client-row" className="border-b border-[#28303F]/50">
                        <td className="py-1 pr-2">
                          <input
                            data-testid={`client-name-${i}`}
                            value={c.name}
                            onChange={e => updClient(i, "name", e.target.value)}
                            className={inputCls}
                            placeholder="Full name"
                          />
                        </td>
                        <td className="py-1 pr-2">
                          <input
                            data-testid={`client-title-${i}`}
                            value={c.title}
                            onChange={e => updClient(i, "title", e.target.value)}
                            className={inputCls}
                            placeholder="Brand Manager"
                          />
                        </td>
                        <td className={`w-28 py-1 pr-2 ${clientColumnClass("phone")}`}>
                          <input
                            data-testid={`client-phone-${i}`}
                            value={c.phone}
                            onChange={e => updClient(i, "phone", e.target.value)}
                            className={inputCls}
                            placeholder="Phone"
                          />
                        </td>
                        <td className={`py-1 pr-2 ${clientColumnClass("email")}`}>
                          <input
                            data-testid={`client-email-${i}`}
                            value={c.email}
                            onChange={e => updClient(i, "email", e.target.value)}
                            className={inputCls}
                            placeholder="Email"
                          />
                        </td>
                        <td className="w-20 py-1 pr-2">
                          <input
                            data-testid={`client-call-time-${i}`}
                            value={c.call_time}
                            onChange={e => updClient(i, "call_time", e.target.value)}
                            className={inputCls}
                            placeholder="09:00"
                          />
                        </td>
                        <td className={`py-1 pr-2 ${clientColumnClass("notes")}`}>
                          <input
                            data-testid={`client-notes-${i}`}
                            value={c.notes}
                            onChange={e => updClient(i, "notes", e.target.value)}
                            className={inputCls}
                            placeholder="Notes"
                          />
                        </td>
                        <td className="py-1">
                          <button
                            data-testid={`client-delete-${i}`}
                            onClick={() => delClient(i)}
                            className="p-1 text-gray-600 hover:text-red-400"
                          >
                            <Trash2 size={13}/>
                          </button>
                        </td>
                      </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <button data-testid="builder-add-client-btn" onClick={addClient}
            className="flex items-center gap-2 text-amber-400 hover:text-amber-300 text-sm mt-2 transition-colors">
            <Plus size={15}/> Add Client
          </button>
        </div>
      </Section>

      {/* ── CREW ASSIGNMENTS ── */}
      <CrewAssignmentsSection form={form} setForm={setForm} project={project}
        crewDb={crew} onAddMember={addCrewFromDB}
        onOpenPicker={() => { setCrewPickerMode("crew"); setShowCrewPicker(true); }} Section={Section} />

      {/* ── NOTES ── */}
      <Section title="Notes & Protocols" defaultOpen={false}>
        <div className="grid grid-cols-1 gap-4 pt-4">
          <F label="Safety Notes" value={form.safety_notes} onChange={set("safety_notes")} type="textarea" />
          <F label="COVID-19 Protocols" value={form.covid_protocols} onChange={set("covid_protocols")} type="textarea" />
          <F label="Walkie Channels" value={form.walkie_channels} onChange={set("walkie_channels")} type="textarea" placeholder="e.g. 1: Production  ·  2: Camera  ·  3: Grip  ·  4: Lock-Up" />
          <F label="Quote of the Day" value={form.quote_of_the_day} onChange={set("quote_of_the_day")} type="textarea" placeholder="A crew-motivating quote to print at the bottom of the call sheet" />
          <F label="Footer" value={form.footer_left_text} onChange={set("footer_left_text")} placeholder="CONFIDENTIAL – NOT FOR DISTRIBUTION" />
        </div>
      </Section>
      </div>

      {/* ── LIVE PREVIEW (desktop only) ── */}
      {showPreview && (
        <aside data-testid="builder-live-preview" className="hidden xl:block w-[620px] shrink-0">
          <div className="sticky top-4 space-y-2">
            <div className="flex items-center justify-between bg-[#1A1F29] border border-[#28303F] rounded-t-xl px-4 py-2.5">
              <div className="flex items-center gap-2">
                <Eye size={14} className="text-amber-400" />
                <p className="text-xs font-semibold text-white uppercase tracking-widest font-mono">Live Preview</p>
                {form.status === "draft" && (
                  <span className="text-[10px] text-red-300 bg-red-500/10 border border-red-500/40 px-2 py-0.5 rounded-full font-mono uppercase tracking-wider">Draft</span>
                )}
              </div>
              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  data-testid="builder-collapse-preview-btn"
                  onClick={() => setShowPreview(false)}
                  title="Hide live preview"
                  className="mr-1 flex h-6 w-6 items-center justify-center rounded border border-[#28303F] text-gray-400 hover:border-amber-500/40 hover:text-amber-400"
                >
                  <EyeOff size={13} />
                </button>
                <button data-testid="builder-preview-zoom-out"
                  onClick={() => setPreviewZoom(z => Math.max(0.35, +(z - 0.05).toFixed(2)))}
                  className="w-6 h-6 flex items-center justify-center text-gray-400 hover:text-amber-400 border border-[#28303F] hover:border-amber-500/40 rounded text-sm">−</button>
                <span data-testid="builder-preview-zoom-value" className="text-[11px] text-gray-400 font-mono w-9 text-center">{Math.round(previewZoom * 100)}%</span>
                <button data-testid="builder-preview-zoom-in"
                  onClick={() => setPreviewZoom(z => Math.min(1.2, +(z + 0.05).toFixed(2)))}
                  className="w-6 h-6 flex items-center justify-center text-gray-400 hover:text-amber-400 border border-[#28303F] hover:border-amber-500/40 rounded text-sm">+</button>
                <button data-testid="builder-preview-zoom-reset"
                  onClick={() => setPreviewZoom(0.65)}
                  className="ml-1 text-[10px] text-gray-500 hover:text-amber-400 px-1.5">Fit</button>
              </div>
            </div>
            <div className="bg-[#0D0F12] border border-[#28303F] border-t-0 rounded-b-xl overflow-auto"
                 style={{ height: "calc(100vh - 120px)" }}>
              <div style={{
                    width: `calc(8.5in * ${previewZoom})`,
                    height: "auto",
                    margin: "12px auto",
                  }}>
                <div style={{
                      transform: `scale(${previewZoom})`,
                      transformOrigin: "top left",
                      width: "8.5in",
                    }}>
                  <CallSheetHtmlPreview
                    cs={form}
                    accentColor={project?.accent_color}
                    logoDataUri={form.logo_data_uri || project?.logo_data_uri}
                    logoAlign={project?.logo_align}
                    logoMaxHeightPt={project?.logo_max_height_pt}
                    isDraft={form.status === "draft"}
                    crewDb={crew}
                    deptOrder={project?.departments || []}
                  />
                </div>
              </div>
            </div>
          </div>
        </aside>
      )}

      {/* Crew Picker Modal — used for both crew_assignments and key_contacts */}
      {showCrewPicker && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-[#14181F] border border-[#28303F] rounded-2xl w-full max-w-lg max-h-[80vh] flex flex-col shadow-2xl">
            <div className="flex items-center justify-between p-5 border-b border-[#28303F]">
              <h3 className="font-bold text-white font-[Outfit]">
                {crewPickerMode === "key_contacts" ? "Pick Key Contact" : "Add from Crew Database"}
              </h3>
              <button onClick={() => setShowCrewPicker(false)} className="text-gray-500 hover:text-white text-2xl leading-none">&times;</button>
            </div>
            <div className="overflow-y-auto flex-1 p-4 space-y-2">
              {crew.length === 0 && <p className="text-gray-500 text-sm text-center py-8">No crew in database yet. Go to Crew Database to add crew.</p>}
              {crew.map(m => {
                const added = crewPickerMode === "key_contacts"
                  ? (form.key_contacts || []).some(c => c.crew_id === m.id)
                  : form.crew_assignments.some(c => c.crew_id === m.id);
                return (
                  <button key={m.id} onClick={() => { addCrewFromDB(m); }}
                    disabled={added}
                    data-testid="crew-picker-item"
                    className={`w-full flex items-center justify-between p-3 rounded-lg border text-left transition-all ${added ? "border-green-500/30 bg-green-500/5 text-gray-500" : "border-[#28303F] hover:border-amber-500/30 hover:bg-[#1A1F29]"}`}>
                    <div>
                      <p className="text-white text-sm font-medium">{m.name}</p>
                      <p className="text-gray-500 text-xs">{m.role || m.position}{m.department ? ` · ${m.department}` : ""}</p>
                    </div>
                    {added ? <span className="text-green-400 text-xs">Added</span> : <Plus size={16} className="text-amber-400"/>}
                  </button>
                );
              })}
            </div>
            <div className="p-4 border-t border-[#28303F]">
              <button onClick={() => setShowCrewPicker(false)}
                className="w-full bg-amber-500 hover:bg-amber-600 text-black font-bold py-2.5 rounded-lg text-sm">Done</button>
            </div>
          </div>
        </div>
      )}

      {/* Template Picker Modal */}
      {showTemplatePicker && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-[#14181F] border border-[#28303F] rounded-2xl w-full max-w-lg max-h-[80vh] flex flex-col shadow-2xl">
            <div className="flex items-center justify-between p-5 border-b border-[#28303F]">
              <h3 className="font-bold text-white font-[Outfit]">Apply Call Sheet Template</h3>
              <button onClick={() => setShowTemplatePicker(false)} className="text-gray-500 hover:text-white text-2xl leading-none">&times;</button>
            </div>
            <div className="overflow-y-auto flex-1 p-4 space-y-2">
              {templates.length === 0 ? (
                <p className="text-gray-500 text-sm text-center py-8">No templates saved yet. Click "Save as Template" to save your current setup.</p>
              ) : templates.map(t => (
                <div key={t.id} className="flex items-center gap-3 p-3 rounded-lg border border-[#28303F] hover:border-amber-500/30 hover:bg-[#1A1F29] transition-all">
                  <button onClick={() => applyTemplate(t)}
                    data-testid="template-apply-item-btn"
                    className="flex-1 text-left">
                    <p className="text-white text-sm font-medium">{t.name}</p>
                    {t.description && <p className="text-gray-500 text-xs mt-0.5">{t.description}</p>}
                    <p className="text-gray-600 text-xs mt-1">
                      {t.dept_calls?.length || 0} depts · {t.nearest_hospital?.name ? "hospital ✓" : "no hospital"} · {t.safety_notes ? "safety ✓" : "no safety"}
                    </p>
                  </button>
                  <button onClick={() => deleteTemplate(t.id)}
                    className="text-gray-600 hover:text-red-400 p-1.5"><Trash2 size={14}/></button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Save Template Modal */}
      {showSaveTemplate && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-[#14181F] border border-[#28303F] rounded-2xl w-full max-w-md shadow-2xl">
            <div className="flex items-center justify-between p-5 border-b border-[#28303F]">
              <h3 className="font-bold text-white font-[Outfit]">Save as Template</h3>
              <button onClick={() => setShowSaveTemplate(false)} className="text-gray-500 hover:text-white text-2xl leading-none">&times;</button>
            </div>
            <div className="p-5 space-y-4">
              <p className="text-xs text-gray-500">Saves: default hospital, dept list, general call, general/safety/COVID notes.</p>
              <div>
                <label className="block text-xs text-gray-500 mb-1.5 uppercase tracking-wider">Template Name *</label>
                <input value={templateName} onChange={e => setTemplateName(e.target.value)}
                  data-testid="template-name-input"
                  placeholder="e.g. Feature Standard, Doc Interior"
                  className="w-full bg-[#0D0F12] border border-[#28303F] rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:border-amber-500" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1.5 uppercase tracking-wider">Description</label>
                <input value={templateDesc} onChange={e => setTemplateDesc(e.target.value)}
                  placeholder="Optional description"
                  className="w-full bg-[#0D0F12] border border-[#28303F] rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:border-amber-500" />
              </div>
              <div className="flex gap-3 pt-2">
                <button onClick={() => setShowSaveTemplate(false)}
                  className="flex-1 border border-[#28303F] text-gray-400 hover:text-white py-2.5 rounded-lg text-sm">Cancel</button>
                <button onClick={saveAsTemplate} disabled={savingTemplate}
                  data-testid="template-save-submit-btn"
                  className="flex-1 bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-black font-bold py-2.5 rounded-lg text-sm">
                  {savingTemplate ? "Saving..." : "Save Template"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Save Location Preset Modal */}
      {showSaveLocation && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-[#14181F] border border-[#28303F] rounded-2xl w-full max-w-md shadow-2xl">
            <div className="flex items-center justify-between p-5 border-b border-[#28303F]">
              <h3 className="font-bold text-white font-[Outfit]">Save Location Preset</h3>
              <button onClick={() => setShowSaveLocation(false)} className="text-gray-500 hover:text-white text-2xl leading-none">&times;</button>
            </div>
            <div className="p-5 space-y-4">
              <p className="text-xs text-gray-500">Saves the current location, parking &amp; hospital as a reusable preset.</p>
              <div>
                <label className="block text-xs text-gray-500 mb-1.5 uppercase tracking-wider">Preset Name *</label>
                <input value={locationName} onChange={e => setLocationName(e.target.value)}
                  data-testid="location-preset-name-input"
                  placeholder="e.g. Downtown Studio, Warehouse Set"
                  className="w-full bg-[#0D0F12] border border-[#28303F] rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:border-amber-500" />
              </div>
              <div className="flex gap-3 pt-2">
                <button onClick={() => setShowSaveLocation(false)}
                  className="flex-1 border border-[#28303F] text-gray-400 hover:text-white py-2.5 rounded-lg text-sm">Cancel</button>
                <button onClick={saveAsLocationPreset} disabled={savingLocation}
                  data-testid="location-preset-save-submit-btn"
                  className="flex-1 bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-black font-bold py-2.5 rounded-lg text-sm">
                  {savingLocation ? "Saving..." : "Save Preset"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
